import { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '@/components/ui/table';
import {
  Clock, Download, ShieldCheck, ShieldAlert, RefreshCw, Calendar, Users, FileText, Loader2,
} from 'lucide-react';
import { fichajesAPI, usuariosAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const fmtFechaHora = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return iso; }
};
const fmtFecha = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-ES'); } catch { return iso; }
};
const fmtMinutos = (m) => {
  if (!m || m < 0) return '0h 00m';
  return `${Math.floor(m / 60)}h ${(m % 60).toString().padStart(2, '0')}m`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);
const firstOfMonthISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

export default function RegistroHorario() {
  const { isMaster } = useAuth();

  // Filtros
  const [desde, setDesde] = useState(firstOfMonthISO());
  const [hasta, setHasta] = useState(todayISO());
  const [usuarioFilter, setUsuarioFilter] = useState('me'); // me | <id> | todos (master)

  // Datos
  const [usuarios, setUsuarios] = useState([]);
  const [fichajes, setFichajes] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [integridad, setIntegridad] = useState(null);
  const [loading, setLoading] = useState(false);

  // Cargar lista de usuarios para master
  useEffect(() => {
    if (!isMaster()) return;
    usuariosAPI.listar({ activo: true })
      .then((res) => setUsuarios(res.data || []))
      .catch(() => setUsuarios([]));
  }, [isMaster]);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const params = { desde, hasta };
      if (isMaster() && usuarioFilter === 'todos') {
        const r = await fichajesAPI.resumenTodos(params);
        setResumen(r.data?.items || []);
        setFichajes([]); // no cargar todos los fichajes individuales aquí
      } else if (isMaster() && usuarioFilter !== 'me') {
        const r = await fichajesAPI.fichajesUsuario(usuarioFilter, params);
        setFichajes(r.data?.items || []);
        setResumen([]);
      } else {
        const r = await fichajesAPI.misRegistros(params);
        setFichajes(r.data?.items || []);
        setResumen([]);
      }
    } catch (e) {
      toast.error('Error al cargar registros');
      setFichajes([]);
      setResumen([]);
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, usuarioFilter, isMaster]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  // Calcular pares entrada/salida + total horas para vista detalle
  const filas = useMemo(() => {
    const out = [];
    let entradaAbierta = null;
    fichajes.forEach((f) => {
      if (f.tipo === 'entrada') {
        if (entradaAbierta) {
          out.push({ entrada: entradaAbierta, salida: null, minutos: null });
        }
        entradaAbierta = f;
      } else if (f.tipo === 'salida') {
        if (entradaAbierta) {
          const ini = new Date(entradaAbierta.timestamp_utc);
          const fin = new Date(f.timestamp_utc);
          const min = Math.max(0, Math.round((fin - ini) / 60000));
          out.push({ entrada: entradaAbierta, salida: f, minutos: min });
          entradaAbierta = null;
        } else {
          out.push({ entrada: null, salida: f, minutos: null });
        }
      }
    });
    if (entradaAbierta) {
      out.push({ entrada: entradaAbierta, salida: null, minutos: null });
    }
    return out;
  }, [fichajes]);

  const totalMinutos = useMemo(
    () => filas.reduce((acc, r) => acc + (r.minutos || 0), 0),
    [filas],
  );

  const handleVerificarIntegridad = async () => {
    try {
      const targetId = (isMaster() && usuarioFilter !== 'me' && usuarioFilter !== 'todos')
        ? usuarioFilter : null;
      const { data } = await fichajesAPI.verificarIntegridad(targetId);
      setIntegridad(data);
      if (data?.integro) {
        toast.success('✓ Cadena de hashes íntegra — registros sin manipular');
      } else {
        toast.warning('Se detectaron alteraciones — revisa el resultado');
      }
    } catch {
      toast.error('No se pudo verificar la integridad');
    }
  };

  const handleExportarCsv = () => {
    const params = { desde, hasta };
    if (isMaster() && usuarioFilter !== 'me' && usuarioFilter !== 'todos') {
      params.usuario_id = usuarioFilter;
    }
    const url = fichajesAPI.exportarCsvUrl(params);
    const token = localStorage.getItem('token');
    // Fetch con auth y descarga
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error('Error al exportar');
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `registro_horario_${desde}_${hasta}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error('Error al exportar CSV'));
  };

  const isResumenView = isMaster() && usuarioFilter === 'todos';

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="registro-horario-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Clock className="w-6 h-6 text-primary" />
            Registro Horario
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Cumplimiento del Real Decreto-ley 8/2019 — fichajes encadenados con SHA-256, exportables para Inspección de Trabajo.
          </p>
        </div>
        <div className="flex gap-2">
          {isMaster() && (
            <Button
              variant="outline"
              onClick={handleVerificarIntegridad}
              className="gap-1.5"
              data-testid="btn-verificar-integridad"
            >
              <ShieldCheck className="w-4 h-4" />
              Verificar integridad
            </Button>
          )}
          {isMaster() && (
            <Button
              variant="default"
              onClick={handleExportarCsv}
              className="gap-1.5"
              data-testid="btn-exportar-csv"
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </Button>
          )}
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            {isMaster() && (
              <div className="space-y-1.5 min-w-[220px]">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  <Users className="w-3 h-3 inline mr-1" />
                  Usuario
                </Label>
                <Select value={usuarioFilter} onValueChange={setUsuarioFilter}>
                  <SelectTrigger className="h-9" data-testid="filtro-usuario">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="me">Yo (master)</SelectItem>
                    <SelectItem value="todos">Todos los usuarios (resumen)</SelectItem>
                    {usuarios.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.nombre} {u.apellidos || ''} · {u.role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                <Calendar className="w-3 h-3 inline mr-1" />
                Desde
              </Label>
              <Input
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                className="h-9 w-40"
                data-testid="filtro-desde"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Hasta</Label>
              <Input
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                className="h-9 w-40"
                data-testid="filtro-hasta"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={cargarDatos}
              disabled={loading}
              className="h-9 gap-1.5"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Actualizar
            </Button>
            {!isResumenView && (
              <div className="ml-auto text-sm text-muted-foreground">
                Total trabajado: <span className="font-semibold text-foreground tabular-nums">{fmtMinutos(totalMinutos)}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Resultado integridad */}
      {integridad && (
        <Card className={integridad.integro ? 'border-green-500/30 bg-green-500/5' : 'border-amber-500/30 bg-amber-500/5'}>
          <CardContent className="p-4 flex items-start gap-3">
            {integridad.integro ? (
              <ShieldCheck className="w-5 h-5 text-green-400 mt-0.5" />
            ) : (
              <ShieldAlert className="w-5 h-5 text-amber-400 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`font-medium text-sm ${integridad.integro ? 'text-green-400' : 'text-amber-400'}`}>
                {integridad.integro
                  ? 'Cadena de hashes íntegra: los registros no han sido manipulados'
                  : 'Se detectaron alteraciones en al menos un usuario'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {integridad.total_usuarios} usuario{integridad.total_usuarios !== 1 ? 's' : ''} verificados.
                {!integridad.integro && (
                  <span> Usuarios con incidencias: {integridad.usuarios.filter(u => u.fichajes_alterados > 0 || u.rotura_secuencia || u.rotura_cadena).map(u => u.usuario_email).join(', ')}.</span>
                )}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setIntegridad(null)}>Cerrar</Button>
          </CardContent>
        </Card>
      )}

      {/* Tabla resumen master (todos los usuarios) */}
      {isResumenView && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              Resumen por usuario
            </CardTitle>
          </CardHeader>
          <CardContent>
            {resumen.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                Sin fichajes en el periodo seleccionado
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-right">Fichajes</TableHead>
                    <TableHead className="text-right">Horas trabajadas</TableHead>
                    <TableHead>Último fichaje</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resumen.map((r) => (
                    <TableRow key={r.usuario_id} data-testid={`resumen-usuario-${r.usuario_id}`}>
                      <TableCell className="font-medium">{r.usuario_nombre || '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.usuario_email}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.fichajes}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{r.horas_trabajadas?.toFixed(2) || '0.00'}h</TableCell>
                      <TableCell className="text-xs">
                        {r.ultimo_fichaje?.tipo && (
                          <Badge variant="outline" className="text-[10px] mr-1">
                            {r.ultimo_fichaje.tipo}
                          </Badge>
                        )}
                        {fmtFechaHora(r.ultimo_fichaje?.timestamp_utc)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabla detalle (mis registros o usuario específico) */}
      {!isResumenView && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              Fichajes del periodo ({fichajes.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {fichajes.length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground">
                Sin fichajes en el periodo seleccionado
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Entrada</TableHead>
                    <TableHead>Salida</TableHead>
                    <TableHead className="text-right">Duración</TableHead>
                    <TableHead>Origen</TableHead>
                    <TableHead className="text-xs">IP</TableHead>
                    <TableHead className="text-xs">Hash</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filas.map((row, idx) => {
                    const ref = row.entrada || row.salida;
                    return (
                      <TableRow key={ref?.id || idx} data-testid={`fila-fichaje-${idx}`}>
                        <TableCell className="text-xs text-muted-foreground tabular-nums">{ref?.secuencia || '—'}</TableCell>
                        <TableCell className="text-xs">{fmtFecha(ref?.timestamp_utc)}</TableCell>
                        <TableCell className="text-xs font-mono">
                          {row.entrada ? fmtFechaHora(row.entrada.timestamp_utc).split(', ')[1] || fmtFechaHora(row.entrada.timestamp_utc) : (
                            <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30">Sin entrada</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs font-mono">
                          {row.salida ? fmtFechaHora(row.salida.timestamp_utc).split(', ')[1] || fmtFechaHora(row.salida.timestamp_utc) : (
                            <Badge variant="outline" className="text-[10px] text-green-400 border-green-500/30">En curso</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-sm font-medium">
                          {row.minutos !== null ? fmtMinutos(row.minutos) : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-[10px]">
                            {ref?.origen || '—'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-[10px] font-mono text-muted-foreground">
                          {ref?.ip || '—'}
                        </TableCell>
                        <TableCell className="text-[10px] font-mono text-muted-foreground" title={ref?.hash_propio}>
                          {ref?.hash_propio?.slice(0, 8)}…
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
